import { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DataTable from "../../core/DataTable.jsx";
import { faUsers } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";

import Loading from "../../layout/Loading.jsx";
import Button from "../../core/Button.jsx";
import SearchInput from "../../input/SearchInput.jsx";
import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall.js";
import useDataColumns from "../../../hooks/useDataColumns.jsx";
import usePagination from "../../../hooks/usePagination.js";
import useSearchTerm from "../../../hooks/useSearchTerm.js";
import useDebounce from "../../../hooks/useDebounce.js";
import { useAuthInfo } from "../../../context/AuthContext.jsx";
import useDelayedLoading from "../../../hooks/useDelayedLoading.js";

const usersPageSize = 25;

const UserListPage = (props) => {
  const defaultTitle = "Users";
  const defaultIcon = faUsers;
  const defaultNewItemPath = "/user-add";

  const baseEndpoint = props.endpoint || "/users";
  const title = props.title || defaultTitle;
  const icon = props.icon || defaultIcon;
  const newItemPath = props.newItemPath || defaultNewItemPath;
  const [searchTerm, setSearchTerm] = useSearchTerm();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterUser, setFilterUser] = useState("active");
  const history = useHistory();
  const [sortParams, setSortParams] = useState(() => {
    const queryParams = new URLSearchParams(history.location.search);
    return {
      sortBy: queryParams.get("sort") || "",
      sortDir: queryParams.get("sort_dir") || "asc",
    };
  });
  const searchDebounce = useDebounce(searchTerm);
  const { userInfo } = useAuthInfo();
  const { userColumns: columns } = useDataColumns();

  const { fetchData, data, isLoading } = useTriggeredAPICall();

  const endpoint = useMemo(() => {
    const params = new URLSearchParams("");
    params.set("sort", sortParams.sortBy);
    params.set("sort_dir", sortParams.sortDir);
    if (searchDebounce) params.set("q", searchDebounce);

    let path;
    if (filterUser === "active") path = baseEndpoint + "/active";
    else if (filterUser === "inactive") path = baseEndpoint + "/inactive";
    else path = baseEndpoint;

    if (!searchDebounce) return `${path}?${params.toString()}`;

    if (filterUser !== "all") return `${path}?${params.toString()}`;
    return `${path}/search?${params.toString()}`;
  }, [filterUser, searchDebounce, sortParams, baseEndpoint]);

  const { currentData, recordsData, listPage } = usePagination(
    data,
    endpoint,
    fetchData
  );

  const showLoading = useDelayedLoading(isLoading);

  const handleCellClick = () => {};

  const handleSelectedRowsChange = useCallback(({ selectedRows }) => {
    setSelectedUsers(selectedRows);
  }, []);

  useEffect(() => {
    let tempSelectedColumns = [];

    if (Array.isArray(props.columns)) {
      tempSelectedColumns = props.columns.map((col) =>
        col?.cell
          ? {
              ...columns[col.id],
              width: col.width || undefined,
              minWidth: col.minWidth || undefined,
              cell: col.cell,
            }
          : {
              ...columns[col.id],
              width: col.width || undefined,
              minWidth: col.minWidth || undefined,
            }
      );
    } else {
      tempSelectedColumns = [
        { ...columns.first_name, width: "15%", minWidth: "105px" },
        {
          ...columns.email,
          width: "25%",
          minWidth: "175px",
          cell: (row) => (
            <div className="text-primary truncate">
              {!row?.email ? "-" : row.email}
            </div>
          ),
        },
        { ...columns.phone, width: "14%", minWidth: "112px" },
        {
          ...columns.orgName,
          width: "10%",
          minWidth: "70px",
          cell: (row) => (
            <div
              className="cursor-pointer text-primary truncate"
              onClick={(e) => {
                e.stopPropagation();
                handleCellClick(row);
              }}
            >
              {!row?.organization?.name ? (
                "-"
              ) : (
                <Link
                  className="table-link truncate"
                  to={`/organization/${row.org_id}`}
                >
                  {row.organization.name}
                </Link>
              )}
            </div>
          ),
        },
        { ...columns.role, width: "9%", minWidth: "63px" },
        { ...columns.is_active, width: "9%", minWidth: "63px" },
      ];
    }

    setSelectedColumns(tempSelectedColumns);
  }, [columns, props.columns]);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);

    if (sortParams.sortBy) {
      params.set("sort", sortParams.sortBy);
      params.set("sort_dir", sortParams.sortDir);
    } else {
      params.delete("sort");
      params.delete("sort_dir");
    }

    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  }, [sortParams, history]);

  useEffect(() => {
    listPage(1);
  }, [endpoint]);

  if (!props.userList && showLoading && !currentData) {
    return <Loading />;
  }

  if (!props.userList && !currentData) {
    return null;
  }

  return (
    <div className="flex-align-start width-full min-width-9 ">
      <h1>
        <FontAwesomeIcon icon={icon} className="users-icon" /> {title}
      </h1>

      {!props.hideSearch && (
        <div className="flex-rows flex-space-between  min-width-9">
          <div className="flex-rows flex-align-center width-50p ">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="width-200px"
            />
            <label>
              <input
                type="checkbox"
                checked={filterUser === "all"}
                onChange={(e) => {
                  setFilterUser("all");
                }}
              />
              <span className="ml-2 mr-4">All</span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={filterUser === "active"}
                onChange={(e) => {
                  setFilterUser("active");
                }}
              />
              <span className="ml-2 mr-4">Active</span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={filterUser === "inactive"}
                onChange={(e) => {
                  setFilterUser("inactive");
                }}
              />
              <span className="ml-2 mr-4">Inactive</span>
            </label>
          </div>
          {(userInfo.role.name !== "user" ||
            props.endpoint === "/contacts") && (
            <Button to={newItemPath}>+ New {title.slice(0, -1)}</Button>
          )}
        </div>
      )}

      <DataTable
        columns={selectedColumns}
        data={props.userList || currentData}
        loading={showLoading}
        selectableRows
        onSelectedRowsChange={handleSelectedRowsChange}
        onPageChange={listPage}
        recordsData={recordsData}
        baseUrl={endpoint}
        setSortParams={setSortParams}
        sortParams={sortParams}
        defaultSortFieldId={sortParams?.sortBy}
        defaultSortAsc={sortParams?.sortDir !== "desc"}
        isSortedManually={true}
      />
    </div>
  );
};

export default UserListPage;
